import React from "react";
import { useTheme } from "@mui/material/styles";

// Generic static table — no sort, no pagination, no search.
// Matches the visual language of AllocationTable (Reports tab) exactly.
//
// Props:
//   title:   string
//   rows:    array of objects (each row keyed by column.key for React key)
//   columns: [{ key, label, align?, render(row, isDark, t) → ReactNode }]

const SimpleTable = ({ title, rows = [], columns = [] }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const th = {
    fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase",
    color: t.text3, padding: "10px 12px", textAlign: "left",
    borderBottom: `1.5px solid ${t.border2}`,
    whiteSpace: "nowrap",
    background: t.surface,
    position: "sticky", top: 0, zIndex: 2,
    userSelect: "none",
  };

  const td = {
    fontSize: 12, color: t.text, padding: "10px 12px",
    borderBottom: `1px solid ${t.border}`,
    verticalAlign: "middle",
  };

  const rowBg = (idx) =>
    idx % 2 === 0
      ? "transparent"
      : isDark ? "rgba(255,255,255,0.02)" : "rgba(3,81,77,0.02)";

  const hoverBg = isDark ? "rgba(40,179,180,0.06)" : "rgba(40,179,180,0.04)";

  return (
    <div style={{
      background:     t.surface,
      border:         `1px solid ${t.border}`,
      borderRadius:   12,
      display:        "flex",
      flexDirection:  "column",
    }}>

      {/* Card header */}
      <div style={{
        display:         "flex",
        alignItems:      "center",
        padding:         "14px 18px",
        borderBottom:    `1px solid ${t.border}`,
        borderRadius:    "12px 12px 0 0",
        background:      t.surface,
        gap:             10,
        flexShrink:      0,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
          {title}
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ ...th, textAlign: col.align ?? "left" }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ ...td, textAlign: "center", padding: "40px 20px" }}
                >
                  <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }}>📋</div>
                  <div style={{ fontSize: 13, color: t.text3 }}>No records found</div>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.id ?? idx}
                  style={{ background: rowBg(idx) }}
                  onMouseEnter={e => (e.currentTarget.style.background = hoverBg)}
                  onMouseLeave={e => (e.currentTarget.style.background = rowBg(idx))}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{ ...td, textAlign: col.align ?? "left" }}
                    >
                      {col.render(row, isDark, t)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SimpleTable;
